import { z } from 'zod';
import { TicketListItemSchema, TicketRefSchema } from './tickets';

/**
 * Busca de tickets por nome, placa e telefone (spec 012).
 *
 * O atendente digita o que o cliente disse — `rtx-4a21`, `(31) 97254-7754`,
 * `joão` — e a busca decide em que campo casar cada forma. A extração do lado
 * do ticket vive no banco (colunas geradas `search_plate`/`search_phones`,
 * migration 1790548293); este módulo é o lado da CONSULTA, e as regras de
 * normalização abaixo são gêmeas das funções SQL `erp.ticket_plate` e
 * `erp.norm_phone`. Divergir aqui é buscar um formato que o banco não guarda.
 */

/** Placa antiga (ABC1234) ou Mercosul (ABC1D23), já sem hífen/espaço. */
const PLATE = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/;
/** Placa começada com ≥ 5 caracteres (`RTX4A`, `RTX4A2`): casa por prefixo. */
const PLATE_PREFIX = /^[A-Z]{3}[0-9][A-Z0-9][0-9]?$/;
/** Só o que alguém digita num telefone: dígitos, +, espaço, parênteses, ponto, hífen. */
const PHONE_CHARS = /^[+\d\s().-]+$/;

/** Mínimo de dígitos para tratar a busca como telefone — `2024` não é (US2.3). */
export const MIN_PHONE_DIGITS = 8;

export interface TicketSearchTerms {
  /** a busca inteira, aparada — o casamento por texto vale sempre */
  text: string;
  /** placa completa normalizada (maiúscula, sem hífen) */
  plate?: string;
  /** início de placa (5–6 caracteres) normalizado — casa por prefixo */
  platePrefix?: string;
  /** só dígitos, sem DDI 55 — casa número inteiro ou sufixo */
  phone?: string;
}

/**
 * Um telefone → só dígitos, sem DDI; `null` se não for telefone. Mesmas regras
 * de `erp.norm_phone`: DDI 55 só sai quando sobram ≥ 10 dígitos (`55 3222-1234`
 * é DDD 55); o nono dígito NÃO é tocado (celular ≠ fixo); < 8 ou > 13 dígitos e
 * dígito repetido (placeholder do SGR) não são telefone.
 */
export function normalizeSearchPhone(raw: string): string | null {
  let d = raw.replace(/\D/g, '');
  if (d.length >= 12 && d.startsWith('55')) d = d.slice(2);
  if (d.length < MIN_PHONE_DIGITS || d.length > 13 || /^(\d)\1+$/.test(d)) return null;
  return d;
}

/** Classifica a busca: placa, telefone e/ou texto (o texto vale sempre). */
export function classifyTicketSearch(q: string): TicketSearchTerms {
  const text = q.trim();
  const terms: TicketSearchTerms = { text };
  if (!text) return terms;

  if (/^[A-Za-z0-9\s-]+$/.test(text)) {
    const compact = text.replace(/[\s-]/g, '').toUpperCase();
    if (PLATE.test(compact)) terms.plate = compact;
    else if (PLATE_PREFIX.test(compact)) terms.platePrefix = compact;
  }

  if (PHONE_CHARS.test(text)) {
    const phone = normalizeSearchPhone(text);
    if (phone) terms.phone = phone;
  }
  return terms;
}

// ── GET /tickets/search — todas as mesas visíveis, agrupado por mesa ─────────

/** Teto da busca agrupada: o resto é refinar a busca, não paginar (spec 012). */
export const TICKET_SEARCH_LIMIT = 200;

export const TicketSearchQuerySchema = z.object({
  q: z.string().trim().min(1).max(200),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(TICKET_SEARCH_LIMIT)
    .default(TICKET_SEARCH_LIMIT),
});
export type TicketSearchQuery = z.infer<typeof TicketSearchQuerySchema>;

export const TicketSearchItemSchema = TicketListItemSchema.extend({
  /** placa extraída do ticket (coluna gerada) — a tela destaca */
  searchPlate: z.string().nullable(),
});
export type TicketSearchItem = z.infer<typeof TicketSearchItemSchema>;

export const TicketSearchGroupSchema = z.object({
  desk: TicketRefSchema,
  /** quantos tickets DESTA mesa casam — inclusive os cortados pelo teto */
  total: z.number().int(),
  items: z.array(TicketSearchItemSchema),
});
export type TicketSearchGroup = z.infer<typeof TicketSearchGroupSchema>;

export const TicketSearchResultSchema = z.object({
  groups: z.array(TicketSearchGroupSchema),
  /** soma dos totais dos grupos */
  total: z.number().int(),
  /** true = há mais tickets do que os itens devolvidos */
  truncated: z.boolean(),
});
export type TicketSearchResult = z.infer<typeof TicketSearchResultSchema>;
